import './index.scss'
import text from './text.json'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import {useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {SimpleBlueButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import {
    advancedFormRef,
    domainFormRef,
    settingsFormRef,
    structureFormRef
} from './refs'
import {
    createStatuspage,
    getStatuspage,
    getStatuspages,
    setSelectedTab,
    updateStatuspage
} from '../../stores/statuspage'
import LimitManager from '../../utils/limitManager'
import TextHelper, {trimStringValues} from '../../utils/text'
import FileManagerService from '../../api/file-manager'
import ObjectManager from '../../utils/object'
import {calculateRemaining} from '../../utils/limits'
import {AdvancedForm, DomainForm, SettingsForm, StructureForm} from './forms'
import {toast} from 'react-toastify'
import {getCustomerMediaUrl} from '../../utils/env'
import renderSkeletonLoaders from '../../utils/loader'
import classNames from 'classnames'
import RenderedForPaid from '../../components/pages/RenderedForPaid'

const CreateEditStatuspage = () => {
    const {id} = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const statuspagesLimit = useMemo(
        () => LimitManager.getStatusPagesLimit(),
        []
    )
    const statuspages = useSelector((state) => state.statuspage.statuspages)
    const statuspageData = id
        ? useSelector((state) => state.statuspage.statuspageData)
        : {}
    const selectedTab = useSelector(
        (state) => state.statuspage.selectedTab
    )
    const isProcessing = useSelector((state) => state.statuspage.isProcessing)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)

    const [subscriptionEnabled, setSubscriptionEnabled] = useState(false)
    const [darkThemeEnabled, setDarkThemeEnabled] = useState(false)
    const [redirectToHttps, setRedirectToHttps] = useState(true)
    const [removeBranding, setRemoveBranding] = useState(false)
    const [logo, setLogo] = useState({})
    const [favicon, setFavicon] = useState({})
    const [logoPath, setLogoPath] = useState(statuspageData?.common?.logo || '')
    const [faviconPath, setFaviconPath] = useState(
        statuspageData?.common?.favicon || ''
    )
    const [logoSignedUrl, setLogoSignedUrl] = useState('')
    const [faviconSignedUrl, setFaviconSignedUrl] = useState('')
    const [currentLogoSrc, setCurrentLogoSrc] = useState(null)
    const [currentFaviconSrc, setCurrentFaviconSrc] = useState(null)
    const [errors, setErrors] = useState({})

    const types = {
        GROUP_TYPE_RESOURCE: 'resource',
        SETTINGS_TYPE_PUBLIC: 'public',
        TAB_SETTINGS: 'settings',
        TAB_DOMAIN: 'custom_domain',
        TAB_ADVANCED: 'advanced',
        TAB_STRUCTURES: 'structures',
        IMAGE_LOGO: 'logo',
        IMAGE_FAVICON: 'favicon'
    }

    const getLimitIsExceeded = () => statuspages?.length >= statuspagesLimit

    const getPaidText = () => {
        return calculateRemaining(statuspagesLimit, statuspages?.length) > 0 && TextHelper.replaceAllChars(
            TextHelper.replaceAllChars(
                text.for_paid,
                ':remaining',
                calculateRemaining(statuspagesLimit, statuspages?.length)
            ),
            ':limit',
            statuspagesLimit
        )
    }

    const getErrorValues = (arr) => {
        const filteredArr = arr.filter((item) => item)
        return filteredArr.flatMap(item =>
            Object.values(item).flatMap(value =>
                Array.isArray(value) ? getErrorValues(value) : [value]
            )
        )
    }

    const hasValidationErrors = (...formErrors) => {
        return formErrors.some(error => error?.length)
    }

    const addOrEditStatusPage = (
        settingsPayload,
        structurePayload,
        advancedPayload,
        domainPayload
    ) => {
        const errorHandlers = [
            domainFormRef?.current?.setFieldError,
            settingsFormRef?.current?.setFieldError
        ]
        if (id) {
            dispatch(
                updateStatuspage(id, trimStringValues({
                    ...settingsPayload,
                    ...structurePayload,
                    ...domainPayload,
                    ...advancedPayload
                }),
                ...errorHandlers
                ))
            return
        }
        dispatch(
            createStatuspage(
                trimStringValues({
                    ...settingsPayload,
                    ...structurePayload,
                    ...domainPayload,
                    ...advancedPayload
                }),
                navigate, ...errorHandlers
            )
        )
    }

    const handleSubmit = async() => {
        const settingsFormErrors = Object.values(
            await settingsFormRef.current.validateForm()
        )
        const structureFormErrors = Object.values(
            await structureFormRef.current?.validateForm()
        )
        const domainFormErrors = Object.values(
            await domainFormRef.current.validateForm()
        )
        const advancedFormErrors = Object.values(
            await advancedFormRef.current.validateForm()
        )

        const settingsValues = settingsFormRef.current.values
        const structureValues = structureFormRef.current.values
        const domainValues = domainFormRef.current.values
        const advancedValues = advancedFormRef.current.values

        if (!hasValidationErrors(settingsFormErrors, structureFormErrors, domainFormErrors, advancedFormErrors)) {
            const components = structureValues?.structures
                ?.filter((item) => item?.is_group.value === false)
                ?.map((component) => {
                    return component.type.value === types.GROUP_TYPE_RESOURCE
                        ? component.components.map((item) => {
                            return {
                                order: component?.order,
                                id: component?.id || '',
                                type: component?.type?.value || component?.type,
                                resource_id: item?.resource?.resource_id,
                                status: 'operational'
                            }
                        })
                        : component.custom_components.map((item) => {
                            return {
                                order: component?.order,
                                id: item?.id || '',
                                type: component?.type?.value || component?.type,
                                name: item?.name,
                                description: item?.description
                            }
                        })
                })

            const structurePayload = {
                groups: structureValues?.structures
                    ?.filter((item) => item?.is_group?.value === true)
                    ?.map((group) => {
                        return {
                            id: group?.id,
                            name: group?.group_name,
                            order: group?.order,
                            components:
                group?.type?.value === types.GROUP_TYPE_RESOURCE
                    ? group?.components?.map((item) => {
                        return {
                            id: item?.id,
                            type: item?.type?.value || item?.type,
                            resource_id: item?.resource?.resource_id,
                            status: item?.status?.value,
                            name: item?.component_name,
                            description: item?.description
                        }
                    })
                    : group?.custom_components?.map((item) => {
                        return {
                            id: item?.id || '',
                            type: group?.type?.value,
                            name: item?.name,
                            description: item?.description
                        }
                    })
                        }
                    }),
                components: components?.reduce((accumulator, currentArray) => {
                    return accumulator.concat(currentArray)
                }, [])
            }

            const settingsPayload = {
                ...settingsValues,
                type: settingsValues?.type?.value,
                ip_addresses: settingsValues?.ip_addresses?.split('\n'),
                dark_theme: darkThemeEnabled,
                password: settingsValues?.password
            }

            const domainPayload = {
                ...domainValues,
                redirect_to_https: redirectToHttps
            }

            const advancedPayload = {
                ...advancedValues,
                robots_meta_contents: settingsValues?.robots_meta_contents?.value
                    ?.split(',')
                    .map((item) => item.trim()),
                subscription_enabled: subscriptionEnabled,
                logo: currentLogoSrc ? logoPath || statuspageData?.common?.logo : '',
                favicon: currentFaviconSrc
                    ? faviconPath || statuspageData?.common?.favicon
                    : '',
                remove_branding: removeBranding
            }

            settingsPayload.update_password =
        statuspageData?.common?.password !== settingsPayload?.password

            if (settingsValues?.type?.value === types.SETTINGS_TYPE_PUBLIC) {
                delete settingsPayload.ip_addresses
                delete settingsPayload.password
            }

            if (!settingsValues?.ip_addresses) {
                delete settingsPayload.ip_addresses
            }

            if (!advancedValues?.robots_meta_contents) {
                delete advancedPayload.robots_meta_contents
            }

            const isLogoUploadSucceeded = logo?.name
                ? await FileManagerService.uploadFile(logoSignedUrl, logo)
                : true
            const isFaviconUploadSucceeded = favicon?.name
                ? await FileManagerService.uploadFile(faviconSignedUrl, favicon)
                : true

            if (isLogoUploadSucceeded && isFaviconUploadSucceeded) {
                await addOrEditStatusPage(
                    settingsPayload,
                    structurePayload,
                    advancedPayload,
                    domainPayload
                )
            }

            setErrors({})
            return
        }

        setErrors({
            settings: settingsFormErrors,
            structures: structureFormErrors,
            custom_domain: domainFormErrors,
            advanced: advancedFormErrors
        })
        toast.error(text.validation_error_msg)
    }

    const generateTabMenu = () => {
        return (
            <div className={classNames('sections', {
                dark: isDarkMode
            })}>
                {text.tab_menu.map((item, index) => (
                    <span
                        onClick={() => {
                            dispatch(setSelectedTab(item.key))
                            navigate(`#${item.key}`)
                        }}
                        className={classNames('section-text', {
                            active: item.key === selectedTab,
                            dark: isDarkMode
                        })}
                        key={index}
                    >
                        {item.name}
                        {errors[item.key]?.length ? (
                            <span className="error-indicator">
                                {item.key !== types.TAB_STRUCTURES ? `(${errors[item.key]?.length})` : `(${getErrorValues(errors[item.key]?.[0])?.length})`}
                                <ul className={classNames('error-list', {
                                    'domain': item.key === types.TAB_DOMAIN,
                                    dark: isDarkMode
                                })}>
                                    {item.key !== types.TAB_STRUCTURES ? errors[item.key].map((error, errorIndex) => (
                                        <li key={errorIndex} className="error-message">
                                            {error}
                                        </li>
                                    )) :
                                        getErrorValues(errors[item.key]?.[0]).map((value, valueIndex) => (
                                            <li key={valueIndex} className="error-message">
                                                {value}
                                            </li>
                                        ))
                                    }
                                </ul>
                            </span>
                        ) : null}
                    </span>
                ))}
            </div>
        )
    }

    const getSignedUrl = async(e, setFieldValue, uploadedFile) => {
        const localFile = uploadedFile || e.target.files[0]
        setFieldValue(`${e.target.name}`, e.target.files[0])
        let extension = ObjectManager.getLastElement(localFile.name.split('.'))
        const modifiedFile = new File([localFile], extension, {
            type: localFile.type,
            size: localFile.size,
            lastModified: localFile.lastModified,
            lastModifiedDate: localFile.lastModifiedDate,
            webkitRelativePath: localFile.webkitRelativePath
        })
        const {data} = await FileManagerService.getImageSignedUrl({
            extension,
            image_type: `${e.target.name}`
        })

        if (e.target.name === 'logo') {
            setLogo(modifiedFile)
            setLogoSignedUrl(data?.data?.signed_url)
            setLogoPath(data?.meta?.file_path)
            return
        }

        if (e.target.name === 'favicon') {
            setFavicon(modifiedFile)
            setFaviconSignedUrl(data?.data?.signed_url)
            setFaviconPath(data?.meta?.file_path)
        }
    }

    const getLogo = () => {
        if (logo?.name) {
            setCurrentLogoSrc(URL.createObjectURL(logo))
            return
        }
        if (statuspageData?.common?.logo) {
            setCurrentLogoSrc(
                getCustomerMediaUrl + statuspageData?.common?.logo
            )
        }
    }

    const getFavicon = () => {
        if (favicon?.name) {
            setCurrentFaviconSrc(URL.createObjectURL(favicon))
            return
        }
        if (statuspageData?.common?.favicon) {
            setCurrentFaviconSrc(
                getCustomerMediaUrl +
          statuspageData?.common?.favicon
            )
        }
    }

    useEffect(() => {
        if (id) {
            dispatch(getStatuspage(id, navigate))
        }
        dispatch(getStatuspages())
        const addressBar = window.location

        if (!addressBar?.hash) {
            navigate(`${addressBar.pathname}#settings`)
        }

        dispatch(setSelectedTab(addressBar.hash.substring(1) || types.TAB_SETTINGS))
    }, [])

    useEffect(() => {
        if (id) {
            getLogo()
        }
    }, [JSON.stringify(statuspageData)])

    useEffect(() => {
        if (id) {
            getFavicon()
        }
    }, [JSON.stringify(statuspageData)])

    useEffect(() => {
        getLogo()
    }, [logoPath])

    useEffect(() => {
        getFavicon()
    }, [faviconPath])

    return (
        <main className={classNames('main create-statuspage', {
            dark: isDarkMode
        })}>
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{id ? text.update : text.create}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <RenderedForPaid
                    forPaidText={getPaidText() || text.for_paid_text_general}
                    isExceeded={getLimitIsExceeded()}
                    isTextWithLimits={!!getPaidText()}
                />
                {generateTabMenu()}
                <>
                    <div
                        className={`settings-container ${
                            selectedTab === types.TAB_SETTINGS ? 'active' : ''
                        }`}
                    >
                        <SettingsForm
                            id={id}
                            statuspageData={statuspageData}
                            darkThemeEnabled={darkThemeEnabled}
                            setDarkThemeEnabled={setDarkThemeEnabled}
                            isLimitExceeded={getLimitIsExceeded()}
                            isDarkMode={isDarkMode}
                        />
                    </div>
                    <div
                        className={`structure-container ${
                            selectedTab === types.TAB_STRUCTURES ? 'active' : ''
                        }`}
                    >
                        <StructureForm
                            id={id}
                            statuspageData={statuspageData}
                            isLimitExceeded={getLimitIsExceeded()}
                            isDarkMode={isDarkMode}
                        />
                    </div>
                    <div
                        className={`domain-container ${
                            selectedTab === types.TAB_DOMAIN ? 'active' : ''
                        }`}
                    >
                        <DomainForm
                            id={id}
                            statuspageData={statuspageData}
                            isLimitExceeded={getLimitIsExceeded()}
                            redirectToHttps={redirectToHttps}
                            setRedirectToHttps={setRedirectToHttps}
                            isDarkMode={isDarkMode}
                        />
                    </div>
                    <div
                        className={`advanced-container ${
                            selectedTab === types.TAB_ADVANCED ? 'active' : ''
                        }`}
                    >
                        <AdvancedForm
                            id={id}
                            statuspageData={statuspageData}
                            isLimitExceeded={getLimitIsExceeded()}
                            removeBranding={removeBranding}
                            setRemoveBranding={setRemoveBranding}
                            currentLogoSrc={currentLogoSrc}
                            setCurrentLogoSrc={setCurrentLogoSrc}
                            getLogo={getLogo}
                            getFavicon={getFavicon}
                            currentFaviconSrc={currentFaviconSrc}
                            setCurrentFaviconSrc={setCurrentFaviconSrc}
                            getSignedUrl={getSignedUrl}
                            subscriptionEnabled={subscriptionEnabled}
                            setSubscriptionEnabled={setSubscriptionEnabled}
                            isDarkMode={isDarkMode}
                        />
                    </div>
                    {isProcessing ? renderSkeletonLoaders(1, 'btn-skeleton') :
                        <SimpleBlueButton
                            disabled={isProcessing || (!id && getLimitIsExceeded())}
                            type="button"
                            onClick={handleSubmit}
                        >
                            {isProcessing && <ButtonLoader />}
                            {text.save}
                        </SimpleBlueButton>}
                </>
            </div>
            <Footer />
        </main>
    )
}

export default CreateEditStatuspage
